// CreateTripWizard — Flujo profesional en 3 pasos:
// 1) Modo (archivos vs manual) + subir PDFs
// 2) Detalles (destinos, fechas, viajeros, intereses, ritmo, dieta)
// 3) Curación IA con progreso + preview + guardar
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Upload, FileText, PencilLine, Plus, X, Sparkles,
  MapPin, Users, Calendar, Utensils, Wallet, Gauge, Check, Loader2, AlertTriangle, ArrowRight,
} from "lucide-react";
import { api } from "../auth/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const INTERESTS = [
  { id: "cultura", label: "Cultura & historia" },
  { id: "gastronomía", label: "Gastronomía" },
  { id: "judío", label: "Herencia judía" },
  { id: "arte", label: "Arte & museos" },
  { id: "arquitectura", label: "Arquitectura" },
  { id: "naturaleza", label: "Naturaleza" },
  { id: "shopping", label: "Shopping" },
  { id: "vida-nocturna", label: "Vida nocturna" },
  { id: "familia", label: "Familia con niños" },
  { id: "aventura", label: "Aventura" },
  { id: "playa", label: "Playa" },
  { id: "religioso", label: "Religioso / espiritual" },
];

const PACES = [
  { id: "relaxed", label: "Relajado", hint: "4-5 lugares/día · siesta obligada" },
  { id: "balanced", label: "Balanceado", hint: "5-7 lugares/día · el clásico" },
  { id: "intense", label: "Intenso", hint: "7-8 lugares/día · aprovechar cada minuto" },
];

const BUDGETS = [
  { id: 1, label: "$", hint: "Barato" },
  { id: 2, label: "$$", hint: "Medio" },
  { id: 3, label: "$$$", hint: "Cómodo" },
  { id: 4, label: "$$$$", hint: "Sin límites" },
];

export default function CreateTripWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [credits, setCredits] = useState(null);

  // Step 1 - mode & files
  const [mode, setMode] = useState(null); // "files" | "manual"
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [parsedFlights, setParsedFlights] = useState([]);
  const [parsedHotels, setParsedHotels] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);

  // Step 2 - trip details
  const [cities, setCities] = useState([{ name: "", nights: 3 }]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [travelers, setTravelers] = useState([{ name: "", age: "" }]);
  const [interests, setInterests] = useState([]);
  const [pace, setPace] = useState("balanced");
  const [budget, setBudget] = useState(2);
  const [dietary, setDietary] = useState("");
  const [freeText, setFreeText] = useState("");

  // Step 3 - curation
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [curated, setCurated] = useState(null);
  const [savingTrip, setSavingTrip] = useState(false);
  const [curationError, setCurationError] = useState(null);

  // Load user credits
  useEffect(() => {
    api("/api/profile")
      .then((p) => setCredits(p.trip_credits || { free_trip_used: false, paid_trips: 0 }))
      .catch(() => {});
  }, []);

  // Derive dates from cities if not set
  useEffect(() => {
    if (parsedFlights.length && !startDate) {
      const dates = parsedFlights.map((f) => f.departDate).filter(Boolean).sort();
      if (dates.length) {
        setStartDate(dates[0]);
        setEndDate(dates[dates.length - 1]);
      }
    }
    if (parsedHotels.length) {
      const inferred = parsedHotels
        .filter((h) => h.city)
        .map((h) => ({ name: h.city, nights: nightsBetween(h.checkIn, h.checkOut) || 2 }));
      if (inferred.length && cities.length === 1 && !cities[0].name) {
        setCities(inferred);
      }
    }
    // eslint-disable-next-line
  }, [parsedFlights, parsedHotels]);

  const totalDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return Math.max(1, Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1);
  }, [startDate, endDate]);

  // ---- Step 1 handlers ----
  const handleFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const arr = Array.from(fileList).slice(0, 5);
    setUploadedFiles(arr);
    setParsing(true);
    setParseError(null);
    try {
      const fd = new FormData();
      arr.forEach((f) => fd.append("files", f));
      const tok = (() => { try { return localStorage.getItem("session_token"); } catch { return null; } })();
      const res = await fetch(`${API}/api/wizard/parse-files`, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (!res.ok) throw new Error(`Extracción falló (${res.status})`);
      const data = await res.json();
      setParsedFlights(data.flights || []);
      setParsedHotels(data.hotels || []);
    } catch (e) {
      setParseError(e.message);
    } finally {
      setParsing(false);
    }
  };

  // ---- Step 3: kick off curation job + polling ----
  const startCuration = async () => {
    setCurationError(null);
    setCurated(null);
    setStep(3);
    try {
      const body = {
        title: cities.map((c) => c.name).filter(Boolean).join(" · "),
        start_date: startDate,
        end_date: endDate,
        cities: cities.filter((c) => c.name).map((c) => ({ name: c.name, nights: Number(c.nights) || 2 })),
        travelers: travelers.filter((t) => t.name).map((t) => ({ name: t.name, age: t.age ? Number(t.age) : undefined })),
        interests,
        pace,
        dietary,
        budget_level: budget,
        free_text: freeText,
        flights: parsedFlights,
        hotels: parsedHotels,
      };
      const res = await api("/api/wizard/curate/start", { method: "POST", body: JSON.stringify(body) });
      setJobId(res.job_id);
    } catch (e) {
      setCurationError(String(e?.message || e));
    }
  };

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        try {
          const r = await api(`/api/wizard/curate/status/${jobId}`);
          if (cancelled) return;
          setJob(r);
          if (r.status === "done") { setCurated(r.result); return; }
          if (r.status === "error") { setCurationError(r.error || "IA falló"); return; }
        } catch (e) {
          if (cancelled) return;
          setCurationError(String(e?.message || e));
          return;
        }
        await new Promise((res) => setTimeout(res, 3000));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [jobId]);

  const saveTrip = async () => {
    if (!curated) return;
    setSavingTrip(true);
    try {
      const res = await api("/api/wizard/create", {
        method: "POST",
        body: JSON.stringify({ trip: curated }),
      });
      navigate(`/viaje/${res.trip_id}`, { replace: true });
    } catch (e) {
      setCurationError(String(e?.message || e));
      setSavingTrip(false);
    }
  };

  const canContinueStep2 =
    startDate && endDate && startDate <= endDate &&
    cities.some((c) => c.name) &&
    travelers.some((t) => t.name) &&
    interests.length > 0;

  // ---------------- UI ----------------
  return (
    <div className="container-app pt-6 pb-24" data-testid="wizard-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => step > 1 ? setStep(step - 1) : navigate("/viajes")}
          className="w-9 h-9 rounded-full flex items-center justify-center bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))]"
          aria-label="Volver"
          data-testid="wizard-back-btn"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">
            Paso {step} de 3
          </p>
          <h1 className="text-2xl font-bold leading-tight">
            {step === 1 ? "¿Cómo quieres empezar?" : step === 2 ? "Cuéntanos del viaje" : "Curando tu viaje…"}
          </h1>
        </div>
      </div>

      {/* Credits pill */}
      {credits && (
        <div
          className="rounded-xl p-3 mb-5 border text-xs"
          style={{
            background: credits.free_trip_used ? "hsl(var(--bg-card))" : "hsl(140 60% 96%)",
            borderColor: credits.free_trip_used ? "hsl(var(--line))" : "hsl(140 50% 80%)",
          }}
          data-testid="wizard-credits"
        >
          {credits.free_trip_used
            ? <>Ya usaste tu viaje gratis · próximo viaje US$5</>
            : <><Sparkles size={12} className="inline mb-0.5" /> Tienes tu <b>viaje gratis</b> disponible</>}
        </div>
      )}

      {/* Steps */}
      {step === 1 && (
        <Step1
          mode={mode} setMode={setMode}
          onNext={() => setStep(2)}
          onFiles={handleFiles}
          uploadedFiles={uploadedFiles}
          parsing={parsing} parseError={parseError}
          parsedFlights={parsedFlights} parsedHotels={parsedHotels}
        />
      )}
      {step === 2 && (
        <Step2
          cities={cities} setCities={setCities}
          startDate={startDate} setStartDate={setStartDate}
          endDate={endDate} setEndDate={setEndDate}
          totalDays={totalDays}
          travelers={travelers} setTravelers={setTravelers}
          interests={interests} setInterests={setInterests}
          pace={pace} setPace={setPace}
          budget={budget} setBudget={setBudget}
          dietary={dietary} setDietary={setDietary}
          freeText={freeText} setFreeText={setFreeText}
          canContinue={canContinueStep2}
          onNext={startCuration}
        />
      )}
      {step === 3 && (
        <Step3
          job={job} curated={curated}
          error={curationError}
          onSave={saveTrip} saving={savingTrip}
          onRetry={startCuration}
        />
      )}
    </div>
  );
}

// ---------------- Step 1 ----------------
function Step1({ mode, setMode, onNext, onFiles, uploadedFiles, parsing, parseError, parsedFlights, parsedHotels }) {
  const inputRef = useRef(null);
  const hasParsed = parsedFlights.length || parsedHotels.length;

  return (
    <div>
      <div className="grid grid-cols-1 gap-3">
        <button
          onClick={() => { setMode("files"); setTimeout(() => inputRef.current?.click(), 100); }}
          className={`card-paper p-5 text-left border-2 transition-colors ${mode === "files" ? "border-[hsl(var(--accent))]" : "border-transparent"}`}
          data-testid="wizard-mode-files"
        >
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] flex items-center justify-center shrink-0">
              <Upload size={22} />
            </div>
            <div>
              <p className="font-bold text-base">Ya tengo mis reservas</p>
              <p className="text-xs text-[hsl(var(--muted))] mt-1">
                Sube PDFs de vuelos, hoteles o boarding passes — la IA extrae los datos por ti.
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setMode("manual")}
          className={`card-paper p-5 text-left border-2 transition-colors ${mode === "manual" ? "border-[hsl(var(--accent))]" : "border-transparent"}`}
          data-testid="wizard-mode-manual"
        >
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] flex items-center justify-center shrink-0">
              <PencilLine size={22} />
            </div>
            <div>
              <p className="font-bold text-base">Planificar desde cero</p>
              <p className="text-xs text-[hsl(var(--muted))] mt-1">
                Sé qué ciudades y fechas quiero — la IA arma todo el itinerario cultural.
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => onFiles(e.target.files)}
        data-testid="wizard-file-input"
      />

      {/* File parsing feedback */}
      {mode === "files" && (
        <div className="mt-5">
          {parsing && (
            <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted))]">
              <Loader2 className="animate-spin" size={14} /> Analizando {uploadedFiles.length} archivo(s) con IA…
            </div>
          )}
          {parseError && (
            <div className="card-paper p-3 text-xs" style={{ color: "hsl(0 70% 45%)" }}>
              <AlertTriangle size={14} className="inline mr-1" />{parseError}
            </div>
          )}
          {!parsing && hasParsed && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted))]">Detectado</p>
              {parsedFlights.map((f, i) => (
                <div key={`f${i}`} className="card-paper p-3 text-xs" data-testid={`parsed-flight-${i}`}>
                  ✈️ <b>{f.airline} {f.flightNumber}</b> · {f.fromCity || f.fromIATA} → {f.toCity || f.toIATA} · {f.departDate} {f.departTime}
                </div>
              ))}
              {parsedHotels.map((h, i) => (
                <div key={`h${i}`} className="card-paper p-3 text-xs" data-testid={`parsed-hotel-${i}`}>
                  🏨 <b>{h.name}</b> · {h.city} · {h.checkIn} → {h.checkOut}
                </div>
              ))}
              <button
                onClick={() => inputRef.current?.click()}
                className="text-xs text-[hsl(var(--accent))] font-semibold"
              >
                + Añadir más archivos
              </button>
            </div>
          )}
        </div>
      )}

      <button
        onClick={onNext}
        disabled={!mode || parsing}
        className="mt-6 w-full h-12 rounded-full bg-[hsl(var(--accent))] text-white font-semibold shadow-lg disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="wizard-step1-next"
      >
        Continuar <ArrowRight size={16} />
      </button>
    </div>
  );
}

// ---------------- Step 2 ----------------
function Step2({
  cities, setCities, startDate, setStartDate, endDate, setEndDate, totalDays,
  travelers, setTravelers, interests, setInterests, pace, setPace,
  budget, setBudget, dietary, setDietary, freeText, setFreeText,
  canContinue, onNext,
}) {
  const toggleInterest = (id) =>
    setInterests(interests.includes(id) ? interests.filter((x) => x !== id) : [...interests, id]);

  return (
    <div className="space-y-6">
      {/* Cities */}
      <Section icon={<MapPin size={14} />} title="Destinos" testId="wizard-cities">
        {cities.map((c, i) => (
          <div key={i} className="flex gap-2 mb-2" data-testid={`wizard-city-${i}`}>
            <input
              value={c.name}
              onChange={(e) => setCities(cities.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
              placeholder="Ciudad (ej: Praga)"
              className="input-field flex-1"
              data-testid={`wizard-city-name-${i}`}
            />
            <input
              type="number"
              min="1"
              value={c.nights}
              onChange={(e) => setCities(cities.map((x, j) => j === i ? { ...x, nights: e.target.value } : x))}
              placeholder="noches"
              className="input-field w-20"
              data-testid={`wizard-city-nights-${i}`}
            />
            {cities.length > 1 && (
              <button
                onClick={() => setCities(cities.filter((_, j) => j !== i))}
                className="w-10 h-10 rounded-lg flex items-center justify-center text-[hsl(var(--muted))]"
                aria-label="Quitar ciudad"
              >
                <X size={16} />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={() => setCities([...cities, { name: "", nights: 2 }])}
          className="text-xs text-[hsl(var(--accent))] font-semibold"
          data-testid="wizard-add-city"
        >
          <Plus size={11} className="inline" /> Añadir ciudad
        </button>
      </Section>

      {/* Dates */}
      <Section icon={<Calendar size={14} />} title="Fechas">
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[hsl(var(--muted))]">Ida</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input-field" data-testid="wizard-start-date" />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[hsl(var(--muted))]">Vuelta</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input-field" data-testid="wizard-end-date" />
          </label>
        </div>
        {totalDays > 0 && (
          <p className="text-xs text-[hsl(var(--muted))] mt-2">
            {totalDays} día{totalDays !== 1 ? "s" : ""} · la IA armará curación día por día
          </p>
        )}
      </Section>

      {/* Travelers */}
      <Section icon={<Users size={14} />} title="Viajeros">
        {travelers.map((t, i) => (
          <div key={i} className="flex gap-2 mb-2" data-testid={`wizard-traveler-${i}`}>
            <input
              value={t.name}
              onChange={(e) => setTravelers(travelers.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
              placeholder="Nombre"
              className="input-field flex-1"
              data-testid={`wizard-traveler-name-${i}`}
            />
            <input
              type="number"
              value={t.age}
              onChange={(e) => setTravelers(travelers.map((x, j) => j === i ? { ...x, age: e.target.value } : x))}
              placeholder="edad"
              className="input-field w-20"
              data-testid={`wizard-traveler-age-${i}`}
            />
            {travelers.length > 1 && (
              <button
                onClick={() => setTravelers(travelers.filter((_, j) => j !== i))}
                className="w-10 h-10 rounded-lg flex items-center justify-center text-[hsl(var(--muted))]"
                aria-label="Quitar viajero"
              >
                <X size={16} />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={() => setTravelers([...travelers, { name: "", age: "" }])}
          className="text-xs text-[hsl(var(--accent))] font-semibold"
          data-testid="wizard-add-traveler"
        >
          <Plus size={11} className="inline" /> Añadir viajero
        </button>
      </Section>

      {/* Interests */}
      <Section icon={<Sparkles size={14} />} title="Intereses (elige los que apliquen)">
        <div className="flex flex-wrap gap-2">
          {INTERESTS.map((it) => (
            <button
              key={it.id}
              onClick={() => toggleInterest(it.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                interests.includes(it.id)
                  ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))]"
                  : "border-[hsl(var(--line))] text-[hsl(var(--ink-soft))]"
              }`}
              data-testid={`wizard-interest-${it.id}`}
            >
              {it.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Pace */}
      <Section icon={<Gauge size={14} />} title="Ritmo del viaje">
        <div className="grid grid-cols-3 gap-2">
          {PACES.map((p) => (
            <button
              key={p.id}
              onClick={() => setPace(p.id)}
              className={`card-paper p-3 text-left transition-colors border-2 ${pace === p.id ? "border-[hsl(var(--accent))]" : "border-transparent"}`}
              data-testid={`wizard-pace-${p.id}`}
            >
              <p className="font-bold text-sm">{p.label}</p>
              <p className="text-[10px] text-[hsl(var(--muted))] mt-0.5 leading-tight">{p.hint}</p>
            </button>
          ))}
        </div>
      </Section>

      {/* Budget */}
      <Section icon={<Wallet size={14} />} title="Presupuesto">
        <div className="grid grid-cols-4 gap-2">
          {BUDGETS.map((b) => (
            <button
              key={b.id}
              onClick={() => setBudget(b.id)}
              className={`card-paper p-3 text-center transition-colors border-2 ${budget === b.id ? "border-[hsl(var(--accent))]" : "border-transparent"}`}
              data-testid={`wizard-budget-${b.id}`}
            >
              <p className="font-bold text-base">{b.label}</p>
              <p className="text-[10px] text-[hsl(var(--muted))] mt-0.5">{b.hint}</p>
            </button>
          ))}
        </div>
      </Section>

      {/* Dietary */}
      <Section icon={<Utensils size={14} />} title="Restricciones alimenticias">
        <input
          value={dietary}
          onChange={(e) => setDietary(e.target.value)}
          placeholder="Ej: sin cerdo, kosher, vegetariano, sin gluten"
          className="input-field w-full"
          data-testid="wizard-dietary"
        />
      </Section>

      {/* Free text — gold for AI */}
      <Section icon={<Sparkles size={14} />} title="Cuéntanos más (opcional pero MUY útil)">
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="Ej: aniversario de bodas, primera vez en Europa, queremos evitar tumultos, mi mujer ama arte contemporáneo…"
          rows={3}
          className="input-field w-full resize-none"
          data-testid="wizard-freetext"
        />
        <p className="text-[10px] text-[hsl(var(--muted))] mt-2">
          Contar contexto real mejora dramáticamente la curación. La IA lo usa para tips y recomendaciones específicas.
        </p>
      </Section>

      <button
        onClick={onNext}
        disabled={!canContinue}
        className="w-full h-12 rounded-full bg-[hsl(var(--accent))] text-white font-semibold shadow-lg disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="wizard-step2-next"
      >
        <Sparkles size={16} /> Curar mi viaje con IA
      </button>
    </div>
  );
}

function Section({ icon, title, children, testId }) {
  return (
    <section data-testid={testId}>
      <h2 className="text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted))] mb-2 flex items-center gap-2">
        {icon} {title}
      </h2>
      {children}
    </section>
  );
}

// ---------------- Step 3 ----------------
function Step3({ job, curated, error, onSave, saving, onRetry }) {
  const progress = job?.total ? Math.round((job.progress / job.total) * 100) : 0;

  if (error) {
    return (
      <div className="card-paper p-5 text-center">
        <AlertTriangle size={28} className="mx-auto mb-3 text-red-500" />
        <p className="font-bold mb-1">Algo falló durante la curación</p>
        <p className="text-xs text-[hsl(var(--muted))] mb-4">{error}</p>
        <button
          onClick={onRetry}
          className="px-4 py-2 rounded-full bg-[hsl(var(--accent))] text-white text-sm font-semibold"
          data-testid="wizard-retry"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!curated) {
    return (
      <div className="card-paper p-5 text-center" data-testid="wizard-curating">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] flex items-center justify-center animate-pulse">
          <Sparkles size={32} />
        </div>
        <p className="font-bold text-lg mb-1">Curando tu viaje…</p>
        <p className="text-xs text-[hsl(var(--muted))] mb-4">
          {job?.current_city
            ? <>Investigando <b>{job.current_city}</b> · día {job.progress} de {job.total}</>
            : "Preparando prompt experto…"}
        </p>
        <div className="w-full h-2 bg-[hsl(var(--line))] rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-[hsl(var(--accent))] transition-all duration-500"
            style={{ width: `${Math.max(4, progress)}%` }}
          />
        </div>
        <p className="text-[10px] text-[hsl(var(--muted))]">
          Un guía turístico especializado está armando cada día · esto toma 30-90 segundos por día
        </p>
      </div>
    );
  }

  // Curated preview
  return (
    <div className="space-y-4" data-testid="wizard-preview">
      <div className="card-paper p-4 border-l-4 border-[hsl(var(--accent))]">
        <p className="text-xs uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Tu viaje</p>
        <p className="font-bold text-lg mt-1">{curated.title}</p>
        <p className="text-xs text-[hsl(var(--muted))]">{curated.subtitle}</p>
        <div className="flex gap-4 mt-3 text-xs">
          <div><b>{curated.days?.length || 0}</b> días</div>
          <div><b>{curated.places?.length || 0}</b> lugares</div>
          <div><b>{curated.restaurants?.length || 0}</b> restaurantes</div>
        </div>
      </div>

      <div className="space-y-3">
        {(curated.days || []).map((d) => (
          <div key={d.n} className="card-paper p-4" data-testid={`preview-day-${d.n}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-wider bg-[hsl(var(--bg-card))] text-[hsl(var(--muted))] px-2 py-0.5 rounded-full font-semibold">
                Día {d.n} · {d.date}
              </span>
              <span className="text-[10px] text-[hsl(var(--muted))]">{d.city}</span>
            </div>
            <p className="font-bold text-sm mb-2">{d.title}</p>
            <p className="text-xs leading-relaxed text-[hsl(var(--ink-soft))] mb-3">
              {d.narrative?.substring(0, 220)}{d.narrative?.length > 220 ? "…" : ""}
            </p>
            <div className="flex flex-wrap gap-1 text-[10px]">
              {(d.stops || []).slice(0, 6).map((s, i) => {
                const p = (curated.places || []).find((x) => x.id === s.id);
                return p ? (
                  <span key={i} className="px-2 py-0.5 rounded-full bg-[hsl(var(--bg-card))] text-[hsl(var(--ink))]">
                    {p.icon || "📍"} {p.name}
                  </span>
                ) : null;
              })}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onSave}
        disabled={saving}
        className="w-full h-12 rounded-full bg-[hsl(var(--accent))] text-white font-semibold shadow-lg disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="wizard-save"
      >
        {saving ? <><Loader2 className="animate-spin" size={16} /> Guardando…</> : <><Check size={16} /> Guardar viaje</>}
      </button>
    </div>
  );
}

// ---------- utils ----------
function nightsBetween(a, b) {
  if (!a || !b) return 0;
  try {
    return Math.round((new Date(b) - new Date(a)) / 86400000);
  } catch { return 0; }
}
